import { forwardRef, useImperativeHandle, useRef } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input, PhoneInput } from "@/shared/ui";
import type { ContactsSection } from "../../../types/personalSheet";
import { editContactsSchema, type EditContactsValues } from "./validation";
import type { SectionFormHandle } from "./types";

interface Props {
  contacts: ContactsSection | null;
  draftValues?: ContactsSection | null;
}

const EditContactsSection = forwardRef<SectionFormHandle, Props>(function EditContactsSection({ contacts, draftValues }, ref) {
  const { t } = useTranslation("EmployeePortal");

  const initialValues = useRef<EditContactsValues>({
    phone: contacts?.phone ?? "",
    phone_additional: contacts?.phone_additional ?? "",
  });

  const displayContacts = draftValues ?? contacts;

  const {
    trigger,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<EditContactsValues>({
    resolver: zodResolver(editContactsSchema),
    mode: "onTouched",
    defaultValues: {
      phone: displayContacts?.phone ?? "",
      phone_additional: displayContacts?.phone_additional ?? "",
    },
  });

  useImperativeHandle(ref, function buildHandle() {
    return {
      getChanges() {
        const current = getValues();
        const initial = initialValues.current;
        const changes: Record<string, unknown> = {};

        if (current.phone !== initial.phone) changes.phone = current.phone;
        if (current.phone_additional !== initial.phone_additional) {
          changes.phone_additional = current.phone_additional;
        }

        return Object.keys(changes).length > 0 ? changes : null;
      },
      getFiles() {
        return null;
      },
      async validate() {
        return await trigger();
      },
      isDirty() {
        const current = getValues();
        const initial = initialValues.current;
        return (
          current.phone !== initial.phone ||
          current.phone_additional !== initial.phone_additional
        );
      },
    };
  });

  return (
    <div className="flex flex-col gap-4">
      <Input
        label={t("personalSheet.fields.email")}
        placeholder=""
        value={contacts?.email ?? ""}
        disabled
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <PhoneInput
          label={t("personalSheet.fields.phone")}
          hasRequiredLabel
          value={watch("phone")}
          onChange={(value) => setValue("phone", value, { shouldDirty: true })}
          error={errors.phone?.message ? t(errors.phone.message) : undefined}
        />

        <PhoneInput
          label={t("personalSheet.fields.phoneAdditional")}
          value={watch("phone_additional")}
          onChange={(value) => setValue("phone_additional", value, { shouldDirty: true })}
          error={errors.phone_additional?.message ? t(errors.phone_additional.message) : undefined}
        />
      </div>
    </div>
  );
});

export default EditContactsSection;
